/**
 * Plugin UI Registry — unified registry for all plugin UI components.
 *
 * All three surfaces (Editor, Web, Play) share this single registry.
 * Plugins are registered via `registerPluginUI()` after dynamic `import()`.
 * Components consume via `pluginUIRegistry.get()`, `.names()`, `.getAll()`.
 *
 * Implements `PluginRegistryLike` so it can be passed directly to
 * `DocumentRenderer` (used by Play's ReadonlyViewer).
 */
import {
    resolvePluginManifest,
    type ConfigPanelProps,
    type PluginSourceInfo,
    type PluginUI,
    type RendererProps,
} from "@magic-md/plugin-sdk";
import { getActiveI18n } from "@magic-md/i18n";
import { createElement, type FC } from "react";
import { PluginSurface } from "../components/PluginSurface";

const plugins = new Map<string, PluginUI>();
const listeners = new Set<() => void>();
const loadingScopes = new Map<string, Set<string> | null>();
let revision = 0;
const localizedPlugins = new WeakMap<
    PluginUI,
    { locale: string; fallbackLocale: string; value: PluginUI }
>();

export interface PluginUILoadSnapshot {
    isLoading: boolean;
    names: string[];
    hasUnknownNames: boolean;
    scopeCount: number;
}

function notify(): void {
    revision += 1;
    for (const fn of listeners) fn();
}

function normalizePluginSource(source: PluginSourceInfo | undefined): PluginSourceInfo {
    return source ?? { kind: "local" };
}

function withDefaultSource(plugin: PluginUI): PluginUI {
    if (plugin.source) return plugin;
    return {
        ...plugin,
        source: normalizePluginSource(undefined),
    };
}

function withIsolatedSurface(plugin: PluginUI): PluginUI {
    const RawConfigPanel = plugin.ConfigPanel;
    const RawRenderer = plugin.Renderer;
    const styles = plugin.styles ?? [];
    const pluginName = plugin.manifest.name;

    const ConfigPanel: FC<ConfigPanelProps> = (props) => createElement(
        PluginSurface,
        { pluginName, display: "block", styles },
        createElement(RawConfigPanel, props),
    );
    ConfigPanel.displayName = `${pluginName}.IsolatedConfigPanel`;

    const Renderer: FC<RendererProps> = (props) => createElement(
        PluginSurface,
        {
            pluginName,
            display: props.mode === "inline" ? "inline" : "block",
            styles,
        },
        createElement(RawRenderer, props),
    );
    Renderer.displayName = `${pluginName}.IsolatedRenderer`;

    return {
        ...plugin,
        styles,
        ConfigPanel,
        Renderer,
    };
}

function localizePluginUI(plugin: PluginUI | undefined): PluginUI | undefined {
    if (!plugin) return undefined;
    const sourcedPlugin = withDefaultSource(plugin);
    if (!plugin.i18n) return sourcedPlugin;
    const activeI18n = getActiveI18n();
    const cached = localizedPlugins.get(sourcedPlugin);
    if (
        cached?.locale === activeI18n.locale &&
        cached.fallbackLocale === activeI18n.fallbackLocale
    ) {
        return cached.value;
    }
    const value = {
        ...sourcedPlugin,
        manifest: resolvePluginManifest(sourcedPlugin.manifest, sourcedPlugin.i18n, {
            locale: activeI18n.locale,
            fallbackLocale: activeI18n.fallbackLocale,
        }),
    };
    localizedPlugins.set(sourcedPlugin, {
        locale: activeI18n.locale,
        fallbackLocale: activeI18n.fallbackLocale,
        value,
    });
    return value;
}

// ── Public API ──

export function registerPluginUI(plugin: PluginUI): void {
    registerPluginUIs([plugin]);
}

export function registerPluginUIs(pluginUIs: Iterable<PluginUI>): void {
    let changed = false;
    for (const plugin of pluginUIs) {
        plugins.set(
            plugin.manifest.name,
            withIsolatedSurface(withDefaultSource(plugin)),
        );
        changed = true;
    }
    if (changed) notify();
}

export function unregisterPluginUI(name: string): boolean {
    const deleted = plugins.delete(name);
    if (deleted) notify();
    return deleted;
}

export function getPluginUI(name: string): PluginUI | undefined {
    return localizePluginUI(plugins.get(name));
}

export function beginPluginUILoad(scope: string, pluginNames?: readonly string[]): void {
    loadingScopes.set(
        scope,
        pluginNames && pluginNames.length > 0 ? new Set(pluginNames) : null,
    );
    notify();
}

export function updatePluginUILoad(scope: string, pluginNames?: readonly string[]): void {
    if (!loadingScopes.has(scope)) return;
    loadingScopes.set(
        scope,
        pluginNames && pluginNames.length > 0 ? new Set(pluginNames) : null,
    );
    notify();
}

export function endPluginUILoad(scope: string): void {
    if (!loadingScopes.delete(scope)) return;
    notify();
}

function isPluginUILoading(name?: string): boolean {
    if (loadingScopes.size === 0) return false;
    if (!name) return true;
    for (const pluginNames of loadingScopes.values()) {
        if (!pluginNames || pluginNames.has(name)) return true;
    }
    return false;
}

function getPluginUILoadSnapshot(): PluginUILoadSnapshot {
    const names = new Set<string>();
    let hasUnknownNames = false;
    for (const pluginNames of loadingScopes.values()) {
        if (!pluginNames) {
            hasUnknownNames = true;
            continue;
        }
        for (const name of pluginNames) names.add(name);
    }
    return {
        isLoading: loadingScopes.size > 0,
        names: [...names].sort(),
        hasUnknownNames,
        scopeCount: loadingScopes.size,
    };
}

/**
 * The pluginUIRegistry singleton — consumed by components and passed as
 * `PluginRegistryLike` prop to `DocumentRenderer`.
 */
export const pluginUIRegistry = {
    get(name: string): PluginUI | undefined {
        return localizePluginUI(plugins.get(name));
    },
    getAll(): Map<string, PluginUI> {
        return new Map(
            [...plugins.entries()].map(([name, plugin]) => [
                name,
                localizePluginUI(plugin) ?? plugin,
            ]),
        );
    },
    names(): string[] {
        return [...plugins.keys()];
    },
    has(name: string): boolean {
        return plugins.has(name);
    },
    revision(): number {
        return revision;
    },
    isLoading(name?: string): boolean {
        return isPluginUILoading(name);
    },
    loadingSnapshot(): PluginUILoadSnapshot {
        return getPluginUILoadSnapshot();
    },
    get size(): number {
        return plugins.size;
    },
    /** Subscribe to registry changes (plugin added/removed). */
    subscribe(listener: () => void): () => void {
        listeners.add(listener);
        return () => listeners.delete(listener);
    },
    /** Clear all registered plugins (used when switching engine modes). */
    clear(): void {
        plugins.clear();
        loadingScopes.clear();
        notify();
    },
};
